import { ACTIONS, type ActionInput, type ActionName, type ActionOutput } from '@occ/contracts';
import { post } from '../api/http';
import { ROUTES } from '../api/routes';

/**
 * Invocación del catálogo de acciones.
 *
 * `POST /actions/:name` es la misma puerta que usa el servidor MCP (P3): el
 * panel no tiene endpoints propios para operar nodos. La entrada se valida
 * contra el catálogo antes de salir —el backend la rechazaría igual, pero así
 * el error se ve en el sitio donde se cometió— y la salida al volver.
 */
export async function runAction<N extends ActionName>(
  name: N,
  input: ActionInput<N>,
): Promise<ActionOutput<N>> {
  const definition = ACTIONS[name];
  const payload: unknown = definition.input.parse(input);
  const result = await post<unknown>(ROUTES.action(name), payload);
  return definition.output.parse(result) as ActionOutput<N>;
}

/** Pestañas de la ficha de nodo que hospedan acciones del catálogo. */
export type NodePanelId = 'terminal' | 'archivos' | 'scripts' | 'servicios';

/**
 * Cómo se dispara cada acción desde el panel.
 *
 * No es una segunda lista de acciones —el catálogo sigue siendo la única
 * fuente— sino la respuesta a una pregunta que el catálogo no contesta: si con
 * la referencia al nodo ya hay bastante para ejecutarla (`run`) o si hace falta
 * pedirle datos al operador antes (`panel`). `services.control` necesita saber
 * qué servicio, `files.delete` qué ruta, `script.run` qué script.
 *
 * Una acción nueva en `@occ/contracts` que no aparezca aquí sale igualmente como
 * botón, apagado y diciendo que le falta pantalla. Eso es deliberado: se ve que
 * existe y que el panel va por detrás, en vez de desaparecer sin más.
 */
export type ActionSurface =
  | { kind: 'run' }
  /**
   * Pide escribir el alias del nodo antes de ejecutar, y **funciona con el nodo
   * apagado**.
   *
   * Las dos cosas van juntas y son de la baja de un nodo. El alias porque el
   * borrado no se deshace y la referencia al nodo puede venir de una fila de una
   * tabla que se reordenó entre que se leyó y se pulsó. Y sin exigir que el nodo
   * esté conectado porque el caso normal de una baja es justamente el contrario:
   * una máquina que se retira suele estar ya apagada, y apagar el botón obligaría
   * a encenderla para poder darla de baja.
   */
  | { kind: 'confirm-alias' }
  | { kind: 'panel'; panel: NodePanelId; verb: string };

const SURFACES: Partial<Record<ActionName, ActionSurface>> = {
  'nodes.get': { kind: 'run' },
  'nodes.inventory': { kind: 'run' },
  'screen.open': { kind: 'run' },
  // Se pinta como JSON (como el inventario): para elegir qué vigilar hay un
  // selector propio en la ficha, que usa esta misma acción.
  'containers.list': { kind: 'run' },

  'nodes.decommission': { kind: 'confirm-alias' },

  'terminal.open': { kind: 'panel', panel: 'terminal', verb: 'Abrir la terminal' },

  'script.run': { kind: 'panel', panel: 'scripts', verb: 'Escribir el script' },

  'services.list': { kind: 'panel', panel: 'servicios', verb: 'Ver los servicios' },
  'services.control': { kind: 'panel', panel: 'servicios', verb: 'Elegir el servicio' },
  'processes.list': { kind: 'panel', panel: 'servicios', verb: 'Ver los procesos' },
  'processes.kill': { kind: 'panel', panel: 'servicios', verb: 'Elegir el proceso' },

  'files.list': { kind: 'panel', panel: 'archivos', verb: 'Navegar los archivos' },
  'files.download': { kind: 'panel', panel: 'archivos', verb: 'Elegir qué bajar' },
  'files.upload': { kind: 'panel', panel: 'archivos', verb: 'Elegir qué subir' },
  'files.delete': { kind: 'panel', panel: 'archivos', verb: 'Elegir qué borrar' },
};

/**
 * Acciones del catálogo cuya pantalla es una tarjeta propia de la ficha y no un
 * botón de «Acciones»: las vigilancias de procesos y contenedores (M16) se ven y se
 * editan en su tarjeta, y un botón suelto aquí no tendría de dónde pedir el nombre ni
 * los canales. Existen en el catálogo para MCP, no para esta botonera.
 */
export const CARD_ACTIONS: ReadonlySet<ActionName> = new Set<ActionName>([
  'watches.list',
  'watches.set',
]);

export const surfaceOf = (name: ActionName): ActionSurface | null => SURFACES[name] ?? null;

/** Acciones que la ficha de nodo sabe disparar sin pedir más datos al operador. */
export const ONE_CLICK_ACTIONS: ActionName[] = (Object.keys(SURFACES) as ActionName[]).filter(
  (name) => SURFACES[name]?.kind === 'run',
);
